import alphaVantageService from './alphaVantageService';
import { supabaseService } from './supabase';
import cacheService from './cacheService';
import {
  FinancialData,
  PricePoint,
  TradingSignal,
  CapeScore,
  FScore,
  PnFChart,
  PnFColumn,
  PnFPattern,
  SignalAction
} from '../types';

// Configuration for the trading strategy
const CONFIG = {
  capeLookbackYears: 8,
  capeEmaSpan: 4,
  pnfBoxSizePercent: 0.01, // 1%
  pnfReversalBoxes: 3,
  pnfPatternType: 'quad-top', // 'triple-top' or 'quad-top'
};

/**
 * Calculate Cyclically Adjusted Price-to-Earnings (CAPE) ratio
 * @param ticker Stock ticker symbol
 * @param financialData Financial data for the ticker
 */
export const calculateCAPE = async (
  ticker: string,
  financialData?: FinancialData
): Promise<CapeScore> => {
  try {
    console.log(`Calculating CAPE for ${ticker}...`);
    
    // Use provided financial data or fetch it
    const data = financialData || await alphaVantageService.getFinancialData(ticker);
    
    // Get current price (most recent closing price)
    const currentPrice = data.priceHistory.length > 0 
      ? data.priceHistory[0].close 
      : await alphaVantageService.getCurrentPrice(ticker);
    
    // Sort income statements by date (most recent first)
    const sortedIncome = [...data.incomeStatement].sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
    
    // Calculate average earnings over lookback period
    const requiredQuarters = CONFIG.capeLookbackYears * 4;
    
    if (sortedIncome.length < requiredQuarters) {
      console.warn(`Insufficient data for ${ticker} CAPE calculation. Need ${requiredQuarters} quarters, have ${sortedIncome.length}.`);
      // Try to work with what we have, but adjust accordingly
      if (sortedIncome.length === 0) {
        return {
          ticker,
          cape: 0,
          capePercentile: 50, // Neutral if we can't calculate
          date: new Date().toISOString().split('T')[0]
        };
      }
    }
    
    // Take the available quarters of earnings (up to required amount)
    const availableQuarters = Math.min(sortedIncome.length, requiredQuarters);
    
    // Extract earnings data, handling potential null/undefined values
    const earningsHistory = sortedIncome
      .slice(0, availableQuarters)
      .map(stmt => {
        // Handle different types of netIncome - could be number or string
        let netIncome = 0;
        if (typeof stmt.netIncome === 'number') {
          netIncome = stmt.netIncome;
        } else if (stmt.netIncome) {
          // Convert to number if it's a string or other type
          const parsedValue = parseFloat(String(stmt.netIncome));
          netIncome = !isNaN(parsedValue) ? parsedValue : 0;
        }
        
        // If netIncome is unreasonably large/small, use 0 as a fallback
        return isFinite(netIncome) && Math.abs(netIncome) < 1e12 
          ? netIncome 
          : 0;
      });

    console.log(`${ticker} earnings data points: ${earningsHistory.length}`);
    
    // Calculate inflation-adjusted average earnings
    // For simplicity, we use EMA to give more weight to recent earnings
    // In a real implementation, we would adjust for inflation properly
    const adjustedEarnings = calculateEMA(earningsHistory, CONFIG.capeEmaSpan);
    
    // Calculate CAPE ratio
    let cape = 0;
    if (adjustedEarnings > 0 && currentPrice > 0) {
      cape = currentPrice / (adjustedEarnings / 4); // Convert quarterly to annual
      
      // Sanity check - CAPE ratios are typically between 5-50
      // Values outside this range may indicate data issues
      if (cape > 200 || cape < 1) {
        console.warn(`${ticker} calculated CAPE (${cape.toFixed(2)}) is outside normal range. Current price: ${currentPrice}, adjusted earnings: ${adjustedEarnings}`);
        
        // Cap extremely high values
        cape = Math.min(cape, 200);
        
        // Ensure non-negative values
        cape = Math.max(cape, 0);
      }
    }
    
    // Log the CAPE calculation
    console.log(`${ticker} CAPE calculation completed: ${cape.toFixed(2)}`);
    
    return {
      ticker,
      cape,
      capePercentile: 50, // Placeholder, will be updated by calculateCAPEPercentiles
      date: new Date().toISOString().split('T')[0]
    };
  } catch (error) {
    console.error(`Error calculating CAPE for ${ticker}:`, error);
    return {
      ticker,
      cape: 0,
      capePercentile: 50,
      date: new Date().toISOString().split('T')[0]
    };
  }
};

/**
 * Calculate Piotroski F-Score for a ticker
 * @param ticker Stock ticker symbol
 * @param financialData Financial data for the ticker
 */
export const calculateFScore = async (
  ticker: string,
  financialData?: FinancialData
): Promise<FScore> => {
  try {
    console.log(`Calculating F-Score for ${ticker}...`);
    
    // Use provided financial data or fetch it
    const data = financialData || await alphaVantageService.getFinancialData(ticker);
    
    // Sort statements by date (most recent first)
    const sortedIncome = [...data.incomeStatement].sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
    
    const sortedBalance = [...data.balanceSheet].sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
    
    const sortedCashFlow = [...data.cashFlow].sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
    
    // Need at least 2 years of data for some calculations
    if (sortedIncome.length < 2 || sortedBalance.length < 2 || sortedCashFlow.length < 1) {
      console.warn(`Insufficient data for ${ticker} F-Score calculation. Income statements: ${sortedIncome.length}, Balance sheets: ${sortedBalance.length}, Cash flows: ${sortedCashFlow.length}`);
      return {
        ticker,
        score: 0,
        roa: false,
        cfo: false,
        deltaRoa: false,
        accrual: false,
        deltaLeverage: false,
        deltaLiquidity: false,
        noEquityRaise: false,
        deltaMargin: false,
        deltaTurnover: false
      };
    }
    
    // Get latest and previous year data
    const latestIncome = sortedIncome[0];
    const prevIncome = sortedIncome[1];
    
    const latestBalance = sortedBalance[0];
    const prevBalance = sortedBalance[1];
    
    const latestCashFlow = sortedCashFlow[0];
    
    // Helper to safely parse numerical values
    const safeParseFloat = (value: any): number => {
      if (typeof value === 'number') return value;
      if (!value) return 0;
      
      const parsed = parseFloat(String(value));
      return isNaN(parsed) || !isFinite(parsed) ? 0 : parsed;
    };
    
    // 1. Return on Assets (ROA) - Positive means 1 point
    const netIncome = safeParseFloat(latestIncome.netIncome);
    const totalAssets = safeParseFloat(latestBalance.totalAssets);
    
    // Skip calculation or use defaults if critical values are missing or zero
    if (totalAssets === 0) {
      console.warn(`${ticker} has zero or invalid total assets, cannot calculate F-Score properly`);
      return {
        ticker,
        score: 0,
        roa: false,
        cfo: false,
        deltaRoa: false,
        accrual: false,
        deltaLeverage: false,
        deltaLiquidity: false,
        noEquityRaise: false,
        deltaMargin: false,
        deltaTurnover: false
      };
    }
    
    const roa = netIncome / totalAssets;
    const fRoa = roa > 0;
    
    // 2. Operating Cash Flow - Positive means 1 point
    const operatingCashFlow = safeParseFloat(latestCashFlow.operatingCashFlow);
    const fCfo = operatingCashFlow > 0;
    
    // 3. Change in ROA - Increase means 1 point
    const prevNetIncome = safeParseFloat(prevIncome.netIncome);
    const prevTotalAssets = safeParseFloat(prevBalance.totalAssets);
    const prevRoa = prevTotalAssets !== 0 ? prevNetIncome / prevTotalAssets : 0;
    const fDeltaRoa = roa > prevRoa;
    
    // 4. Accruals (CFO > ROA) - True means 1 point
    const cfRoa = totalAssets !== 0 ? operatingCashFlow / totalAssets : 0;
    const fAccrual = cfRoa > roa;
    
    // 5. Change in Leverage - Decrease means 1 point
    // Use totalLiabilities if available, otherwise approximate from other fields
    let totalLiabilities = safeParseFloat(latestBalance.totalLiabilities);
    if (totalLiabilities === 0 && latestBalance.totalDebt) {
      totalLiabilities = safeParseFloat(latestBalance.totalDebt);
    }
    
    let prevTotalLiabilities = safeParseFloat(prevBalance.totalLiabilities);
    if (prevTotalLiabilities === 0 && prevBalance.totalDebt) {
      prevTotalLiabilities = safeParseFloat(prevBalance.totalDebt);
    }
    
    // Calculate leverage ratio
    const leverage = totalAssets !== 0 ? totalLiabilities / totalAssets : 0;
    const prevLeverage = prevTotalAssets !== 0 ? prevTotalLiabilities / prevTotalAssets : 0;
    const fLeverage = leverage < prevLeverage;
    
    // 6. Change in Current Ratio - Increase means 1 point
    const currentAssets = safeParseFloat(latestBalance.currentAssets);
    const currentLiabilities = safeParseFloat(latestBalance.currentLiabilities);
    const prevCurrentAssets = safeParseFloat(prevBalance.currentAssets);
    const prevCurrentLiabilities = safeParseFloat(prevBalance.currentLiabilities);
    
    // Calculate current ratios, avoiding division by zero
    const currentRatio = currentLiabilities !== 0 ? currentAssets / currentLiabilities : 0;
    const prevCurrentRatio = prevCurrentLiabilities !== 0 ? prevCurrentAssets / prevCurrentLiabilities : 0;
    const fLiquidity = currentRatio > prevCurrentRatio;
    
    // 7. Change in Shares Outstanding - No new shares means 1 point
    // Some companies don't report shares outstanding, so we need to handle that
    let sharesOutstanding = 0;
    let prevSharesOutstanding = 0;
    
    if (latestBalance.sharesOutstanding !== undefined) {
      sharesOutstanding = safeParseFloat(latestBalance.sharesOutstanding);
    }
    
    if (prevBalance.sharesOutstanding !== undefined) {
      prevSharesOutstanding = safeParseFloat(prevBalance.sharesOutstanding);
    }
    
    // If we don't have shares outstanding data, default to true (giving benefit of doubt)
    const fEquityOffering = sharesOutstanding === 0 || prevSharesOutstanding === 0 ? 
      true : sharesOutstanding <= prevSharesOutstanding;
    
    // 8. Change in Gross Margin - Increase means 1 point
    const grossProfit = safeParseFloat(latestIncome.grossProfit);
    const totalRevenue = safeParseFloat(latestIncome.totalRevenue);
    const prevGrossProfit = safeParseFloat(prevIncome.grossProfit);
    const prevTotalRevenue = safeParseFloat(prevIncome.totalRevenue);
    
    // Calculate margins, avoiding division by zero
    const grossMargin = totalRevenue !== 0 ? grossProfit / totalRevenue : 0;
    const prevGrossMargin = prevTotalRevenue !== 0 ? prevGrossProfit / prevTotalRevenue : 0;
    const fMargin = grossMargin > prevGrossMargin;
    
    // 9. Change in Asset Turnover - Increase means 1 point
    const assetTurnover = totalAssets !== 0 ? totalRevenue / totalAssets : 0;
    const prevAssetTurnover = prevTotalAssets !== 0 ? prevTotalRevenue / prevTotalAssets : 0;
    const fTurnover = assetTurnover > prevAssetTurnover;
    
    // Calculate total F-Score (sum of all 9 binary signals)
    const score = (fRoa ? 1 : 0) + 
                  (fCfo ? 1 : 0) + 
                  (fDeltaRoa ? 1 : 0) + 
                  (fAccrual ? 1 : 0) + 
                  (fLeverage ? 1 : 0) + 
                  (fLiquidity ? 1 : 0) + 
                  (fEquityOffering ? 1 : 0) + 
                  (fMargin ? 1 : 0) + 
                  (fTurnover ? 1 : 0);
    
    console.log(`${ticker} F-Score calculation completed: ${score}/9`);
    
    return {
      ticker,
      score,
      roa: fRoa,
      cfo: fCfo,
      deltaRoa: fDeltaRoa,
      accrual: fAccrual,
      deltaLeverage: fLeverage,
      deltaLiquidity: fLiquidity,
      noEquityRaise: fEquityOffering,
      deltaMargin: fMargin,
      deltaTurnover: fTurnover
    };
  } catch (error) {
    console.error(`Error calculating F-Score for ${ticker}:`, error);
    return {
      ticker,
      score: 0,
      roa: false,
      cfo: false,
      deltaRoa: false,
      accrual: false,
      deltaLeverage: false,
      deltaLiquidity: false,
      noEquityRaise: false,
      deltaMargin: false,
      deltaTurnover: false
    };
  }
};

/**
 * Generate Point & Figure Chart from price data
 * @param ticker Stock ticker symbol
 * @param priceData Array of price points
 */
export const generatePnFChart = (
  ticker: string,
  priceData: PricePoint[]
): PnFChart | null => {
  try {
    console.log(`Generating Point & Figure chart for ${ticker}...`);
    
    if (!priceData || priceData.length < 20) {
      console.warn(`Insufficient price data for ${ticker} P&F chart (need at least 20 data points, got ${priceData?.length || 0})`);
      return null;
    }
    
    // Sort price data by date (newest first)
    const sortedPrices = [...priceData].sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
    
    // Extract closing prices
    const prices = sortedPrices.map(p => p.close);
    
    // Get current price and calculate box size
    const currentPrice = prices[0];
    console.log(`${ticker} current price: ${currentPrice}`);
    
    // Calculate box size based on price level for more appropriate scaling
    let boxSizePercent = CONFIG.pnfBoxSizePercent;
    
    // Adjust box size percent based on price level
    if (currentPrice < 10) {
      // For low-priced stocks, use larger percentage to avoid too many small boxes
      boxSizePercent = 0.02; // 2%
    } else if (currentPrice > 1000) {
      // For high-priced stocks, use smaller percentage
      boxSizePercent = 0.005; // 0.5%
    }
    
    const boxSize = currentPrice * boxSizePercent;
    const reversalSize = CONFIG.pnfReversalBoxes * boxSize;
    
    console.log(`${ticker} P&F chart parameters: Box size: $${boxSize.toFixed(2)} (${(boxSizePercent * 100).toFixed(1)}%), Reversal size: $${reversalSize.toFixed(2)}`);
    
    // Initialize P&F chart
    const pnfChart: PnFChart = {
      ticker,
      columns: [],
      currentPrice,
      patterns: {
        tripleTopBreakout: false,
        tripleTopBreakdown: false,
        quadTopBreakout: false,
        quadTopBreakdown: false
      }
    };
    
    // Use the oldest price as starting point
    let lastPrice = prices[prices.length - 1];
    
    // Initialize first column with the first price
    let currentDirection: 'up' | 'down' = 'up';
    let currentColumn: PnFColumn = {
      direction: currentDirection,
      boxes: [lastPrice],
      high: lastPrice,
      low: lastPrice
    };
    
    pnfChart.columns.push(currentColumn);
    
    // Process each price to build the P&F chart
    for (let i = prices.length - 2; i >= 0; i--) {
      const price = prices[i];
      
      // Skip invalid prices
      if (!isFinite(price) || price <= 0) {
        console.warn(`${ticker} has invalid price at index ${i}, skipping`);
        continue;
      }
      
      currentColumn = pnfChart.columns[pnfChart.columns.length - 1];
      
      if (currentDirection === 'up') {
        // In an up column
        if (price >= currentColumn.high + boxSize) {
          // Price moved up by at least one box
          const newBoxes = Math.floor((price - currentColumn.high) / boxSize);
          for (let j = 0; j < newBoxes; j++) {
            const newPrice = currentColumn.high + boxSize;
            currentColumn.boxes.push(newPrice);
            currentColumn.high = newPrice;
          }
        } else if (price <= currentColumn.high - reversalSize) {
          // Reversal down by at least 'reversalSize'
          // Start a new column
          currentDirection = 'down';
          
          // Calculate how many boxes down
          const startPrice = currentColumn.high;
          const newBoxes = Math.floor((startPrice - price) / boxSize);
          
          const newColumn: PnFColumn = {
            direction: 'down',
            boxes: [],
            high: startPrice,
            low: startPrice
          };
          
          // Add boxes
          for (let j = 0; j < newBoxes; j++) {
            const newPrice = startPrice - (j + 1) * boxSize;
            newColumn.boxes.push(newPrice);
            newColumn.low = newPrice;
          }
          
          pnfChart.columns.push(newColumn);
        }
      } else {
        // In a down column
        if (price <= currentColumn.low - boxSize) {
          // Price moved down by at least one box
          const newBoxes = Math.floor((currentColumn.low - price) / boxSize);
          for (let j = 0; j < newBoxes; j++) {
            const newPrice = currentColumn.low - boxSize;
            currentColumn.boxes.push(newPrice);
            currentColumn.low = newPrice;
          }
        } else if (price >= currentColumn.low + reversalSize) {
          // Reversal up by at least 'reversalSize'
          // Start a new column
          currentDirection = 'up';
          
          // Calculate how many boxes up
          const startPrice = currentColumn.low;
          const newBoxes = Math.floor((price - startPrice) / boxSize);
          
          const newColumn: PnFColumn = {
            direction: 'up',
            boxes: [],
            high: startPrice,
            low: startPrice
          };
          
          // Add boxes
          for (let j = 0; j < newBoxes; j++) {
            const newPrice = startPrice + (j + 1) * boxSize;
            newColumn.boxes.push(newPrice);
            newColumn.high = newPrice;
          }
          
          pnfChart.columns.push(newColumn);
        }
      }
    }
    
    // Identify patterns
    identifyPnFPatterns(pnfChart);
    
    // Log the chart details
    console.log(`${ticker} P&F chart completed with ${pnfChart.columns.length} columns`);
    if (pnfChart.patterns.tripleTopBreakout || pnfChart.patterns.quadTopBreakout) {
      console.log(`${ticker} shows a bullish breakout pattern`);
    } else if (pnfChart.patterns.tripleTopBreakdown || pnfChart.patterns.quadTopBreakdown) {
      console.log(`${ticker} shows a bearish breakdown pattern`);
    }
    
    return pnfChart;
  } catch (error) {
    console.error(`Error generating P&F chart for ${ticker}:`, error);
    return null;
  }
};

/**
 * Identify Triple/Quad-Top breakout and breakdown patterns
 * @param pnfChart Point & Figure chart
 */
function identifyPnFPatterns(pnfChart: PnFChart): void {
  const columns = pnfChart.columns;
  const requiredTops = CONFIG.pnfPatternType === 'triple-top' ? 3 : 4;
  
  if (columns.length < requiredTops + 1) {
    // Need at least requiredTops + 1 columns for a proper pattern
    return;
  }
  
  // Extract tops and bottoms
  const tops: { index: number, value: number }[] = [];
  const bottoms: { index: number, value: number }[] = [];
  
  columns.forEach((col, i) => {
    if (col.direction === 'up') {
      tops.push({ index: i, value: col.high });
    } else {
      bottoms.push({ index: i, value: col.low });
    }
  });
  
  // Check for Triple/Quad-Top Breakout
  if (tops.length >= requiredTops) {
    // Check the last N tops for a breakout pattern
    // We want these tops to be consecutive without interruption
    let consecutiveTops = 0;
    let lastTopIndex = -1;
    let patternTopValue = 0;
    
    // Start from most recent top and go backward
    for (let i = tops.length - 1; i >= 0; i--) {
      const top = tops[i];
      
      // If this is first top or it's consecutive with previous one
      if (lastTopIndex === -1 || top.index === lastTopIndex - 1) {
        // If this is the first top in our pattern or it equals our pattern value
        if (consecutiveTops === 0 || Math.abs(top.value - patternTopValue) < 0.001) {
          if (consecutiveTops === 0) {
            patternTopValue = top.value;
          }
          consecutiveTops++;
          lastTopIndex = top.index;
          
          // Check if we found enough consecutive equal tops
          if (consecutiveTops >= requiredTops) {
            // Now check if there's a breakout after this pattern
            // We need a top with higher value after our pattern
            for (let j = 0; j < i; j++) {
              if (tops[j].value > patternTopValue) {
                if (requiredTops === 3) {
                  pnfChart.patterns.tripleTopBreakout = true;
                } else {
                  pnfChart.patterns.quadTopBreakout = true;
                }
                break;
              }
            }
            break;
          }
        } else {
          // Reset if we find a top that doesn't match our pattern
          consecutiveTops = 1;
          patternTopValue = top.value;
          lastTopIndex = top.index;
        }
      } else {
        // Reset if we find a non-consecutive top
        consecutiveTops = 1;
        patternTopValue = top.value;
        lastTopIndex = top.index;
      }
    }
  }
  
  // Check for Triple/Quad-Top Breakdown - similar to above but for bottoms
  if (bottoms.length >= requiredTops) {
    let consecutiveBottoms = 0;
    let lastBottomIndex = -1;
    let patternBottomValue = 0;
    
    // Start from most recent bottom and go backward
    for (let i = bottoms.length - 1; i >= 0; i--) {
      const bottom = bottoms[i];
      
      // If this is first bottom or it's consecutive with previous one
      if (lastBottomIndex === -1 || bottom.index === lastBottomIndex - 1) {
        // If this is the first bottom in our pattern or it equals our pattern value
        if (consecutiveBottoms === 0 || Math.abs(bottom.value - patternBottomValue) < 0.001) {
          if (consecutiveBottoms === 0) {
            patternBottomValue = bottom.value;
          }
          consecutiveBottoms++;
          lastBottomIndex = bottom.index;
          
          // Check if we found enough consecutive equal bottoms
          if (consecutiveBottoms >= requiredTops) {
            // Now check if there's a breakdown after this pattern
            // We need a bottom with lower value after our pattern
            for (let j = 0; j < i; j++) {
              if (bottoms[j].value < patternBottomValue) {
                if (requiredTops === 3) {
                  pnfChart.patterns.tripleTopBreakdown = true;
                } else {
                  pnfChart.patterns.quadTopBreakdown = true;
                }
                break;
              }
            }
            break;
          }
        } else {
          // Reset if we find a bottom that doesn't match our pattern
          consecutiveBottoms = 1;
          patternBottomValue = bottom.value;
          lastBottomIndex = bottom.index;
        }
      } else {
        // Reset if we find a non-consecutive bottom
        consecutiveBottoms = 1;
        patternBottomValue = bottom.value;
        lastBottomIndex = bottom.index;
      }
    }
  }
}

/**
 * Helper function to calculate exponential moving average
 * @param data Array of numbers
 * @param span EMA span
 */
function calculateEMA(data: number[], span: number): number {
  if (data.length === 0) return 0;
  
  const alpha = 2 / (span + 1);
  let ema = data[0];
  
  for (let i = 1; i < data.length; i++) {
    ema = alpha * data[i] + (1 - alpha) * ema;
  }
  
  return ema;
}

/**
 * Generate trading signal based on the Triple Point strategy
 * @param ticker Stock symbol
 * @param financialData Optional financial data for performance
 */
export const generateTradingSignal = async (
  ticker: string,
  financialData?: FinancialData
): Promise<TradingSignal> => {
  try {
    console.log(`Generating trading signal for ${ticker}...`);
    
    // Check if we have a cached signal
    const cacheKey = `trading_signal_${ticker}`;
    const cachedSignal = await cacheService.getCachedData(
      cacheKey,
      async () => {
        // Fetch financial data if not provided
        const data = financialData || await alphaVantageService.getFinancialData(ticker);
        
        // Calculate CAPE score
        const capeScore = await calculateCAPE(ticker, data);
        
        // Calculate F-Score
        const fScore = await calculateFScore(ticker, data);
        
        // Generate P&F chart
        const pnfChart = generatePnFChart(ticker, data.priceHistory);
        
        // If we couldn't generate one of the components, use cautious approach
        if (!capeScore || !fScore || !pnfChart) {
          console.warn(`Missing component data for ${ticker}, defaulting to Hold signal`);
          return {
            ticker,
            cape: capeScore?.cape || 0,
            capePercentile: capeScore?.capePercentile || 50,
            f_score: fScore?.score || 0,
            preliminarySignal: 'Hold' as SignalAction,
            finalSignal: 'Hold' as SignalAction,
            date: new Date().toISOString().split('T')[0]
          };
        }
        
        // Apply the Triple Point strategy logic
        
        // 1. Determine preliminary signal based on CAPE and F-Score
        // CAPE below 20th percentile + F-Score of 6+ indicates good value
        // CAPE above 80th percentile + F-Score of 4 or below indicates overvalued
        let preliminarySignal: SignalAction = 'Hold';
        
        // Value signal
        if (capeScore.capePercentile <= 20 && fScore.score >= 6) {
          preliminarySignal = 'Buy';
          console.log(`${ticker} generated preliminary BUY signal: Low CAPE (${capeScore.capePercentile}%) + High F-Score (${fScore.score})`);
        } 
        // Overvalued signal
        else if (capeScore.capePercentile >= 80 && fScore.score <= 4) {
          preliminarySignal = 'Sell';
          console.log(`${ticker} generated preliminary SELL signal: High CAPE (${capeScore.capePercentile}%) + Low F-Score (${fScore.score})`);
        }
        // Additional conditions for improved accuracy
        else if (capeScore.capePercentile <= 30 && fScore.score >= 5) {
          // Slightly less strict value condition
          preliminarySignal = 'Buy';
          console.log(`${ticker} generated secondary BUY signal: Moderate-Low CAPE (${capeScore.capePercentile}%) + Decent F-Score (${fScore.score})`);
        }
        else if (capeScore.capePercentile >= 70 && fScore.score <= 3) {
          // Slightly less strict overvalued condition
          preliminarySignal = 'Sell';
          console.log(`${ticker} generated secondary SELL signal: Moderate-High CAPE (${capeScore.capePercentile}%) + Poor F-Score (${fScore.score})`);
        }
        else {
          console.log(`${ticker} generated HOLD signal from fundamental analysis: CAPE (${capeScore.capePercentile}%) + F-Score (${fScore.score})`);
        }
        
        // 2. Determine final signal based on P&F confirmation
        let finalSignal: SignalAction = 'Hold';
        
        const tripleTopBreakout = pnfChart.patterns.tripleTopBreakout;
        const quadTopBreakout = pnfChart.patterns.quadTopBreakout;
        const tripleTopBreakdown = pnfChart.patterns.tripleTopBreakdown;
        const quadTopBreakdown = pnfChart.patterns.quadTopBreakdown;
        
        // Required pattern based on configuration
        const breakoutPattern = CONFIG.pnfPatternType === 'triple-top' ? tripleTopBreakout : quadTopBreakout;
        const breakdownPattern = CONFIG.pnfPatternType === 'triple-top' ? tripleTopBreakdown : quadTopBreakdown;
        
        if (preliminarySignal === 'Buy' && (breakoutPattern || tripleTopBreakout)) {
          // If we have a preliminary Buy and a technical breakout, confirm the Buy
          finalSignal = 'Buy';
          console.log(`${ticker} CONFIRMED BUY signal with ${CONFIG.pnfPatternType} breakout pattern`);
        } else if (preliminarySignal === 'Sell' && (breakdownPattern || tripleTopBreakdown)) {
          // If we have a preliminary Sell and a technical breakdown, confirm the Sell
          finalSignal = 'Sell';
          console.log(`${ticker} CONFIRMED SELL signal with ${CONFIG.pnfPatternType} breakdown pattern`);
        } else {
          // No confirmation = Hold
          finalSignal = 'Hold';
          console.log(`${ticker} downgraded to HOLD - no ${CONFIG.pnfPatternType} pattern confirmation`);
        }
        
        // Create the trading signal
        const tradingSignal: TradingSignal = {
          ticker,
          cape: capeScore.cape,
          capePercentile: capeScore.capePercentile,
          f_score: fScore.score,
          preliminarySignal,
          finalSignal,
          date: new Date().toISOString().split('T')[0]
        };
        
        return tradingSignal;
      },
      { 
        ttl: 24 * 60 * 60 * 1000, // 24 hours
        useLocalStorage: true
      }
    );
    
    return cachedSignal;
  } catch (error) {
    console.error(`Error generating trading signal for ${ticker}:`, error);
    
    // Return a default "Hold" signal in case of error
    return {
      ticker,
      cape: 0,
      capePercentile: 50,
      f_score: 0,
      preliminarySignal: 'Hold' as SignalAction,
      finalSignal: 'Hold' as SignalAction,
      date: new Date().toISOString().split('T')[0]
    };
  }
};

/**
 * Calculate CAPE percentiles for a set of stocks
 * This ranks stocks by their CAPE ratios and determines their relative valuation
 * @param capeScores Array of CAPE scores for different stocks
 */
export const calculateCAPEPercentiles = (capeScores: CapeScore[]): CapeScore[] => {
  try {
    console.log(`Calculating CAPE percentiles for ${capeScores.length} stocks...`);
    
    // Filter out invalid CAPE values (zero or negative)
    const validScores = capeScores.filter(score => score.cape > 0);
    
    if (validScores.length === 0) {
      console.warn('No valid CAPE scores to calculate percentiles');
      return capeScores;
    }
    
    // Sort CAPE scores from lowest to highest
    const sortedScores = [...validScores].sort((a, b) => a.cape - b.cape);
    
    // Calculate historical average if we have enough data
    const sumCape = sortedScores.reduce((sum, score) => sum + score.cape, 0);
    const averageCape = sumCape / sortedScores.length;
    
    console.log(`Average CAPE across ${sortedScores.length} stocks: ${averageCape.toFixed(2)}`);
    
    // Assign percentiles based on rank
    const updatedScores = capeScores.map(score => {
      if (score.cape <= 0) {
        // Invalid CAPE - assign neutral percentile
        return {
          ...score,
          capePercentile: 50,
          historicalAverage: averageCape
        };
      }
      
      // Find position of this score in sorted array
      const index = sortedScores.findIndex(s => s.ticker === score.ticker);
      
      if (index === -1) {
        // Score not found in sorted array (shouldn't happen)
        return {
          ...score,
          capePercentile: 50,
          historicalAverage: averageCape
        };
      }
      
      // Calculate percentile (0-100)
      // Lower CAPE = lower percentile = better value
      const percentile = Math.round((index / (sortedScores.length - 1)) * 100);
      
      return {
        ...score,
        capePercentile: percentile,
        historicalAverage: averageCape
      };
    });
    
    return updatedScores;
  } catch (error) {
    console.error('Error calculating CAPE percentiles:', error);
    return capeScores;
  }
}

/**
 * Generate trading signals for all S&P 500 stocks
 * This is a computationally expensive operation, so it's cached
 * @param forceRefresh Force a refresh of the signals, bypassing the cache
 */
export const generateAllTradingSignals = async (forceRefresh: boolean = false): Promise<TradingSignal[]> => {
  try {
    console.log('Generating all trading signals with forceRefresh:', forceRefresh);
    
    // Get signals from cache or compute them
    return await cacheService.getCachedData(
      'all_trading_signals',
      async () => {
        // Get all tickers from Alpha Vantage
        console.log('Fetching tickers from Alpha Vantage API...');
        const tickers = await alphaVantageService.getSP500Tickers();
        console.log(`Fetched ${tickers.length} tickers from Alpha Vantage API`);
        
        // Process ALL tickers without limitation
        let tickersToProcess = tickers;
        console.log(`Processing ALL ${tickersToProcess.length} S&P 500 stocks - no limitations applied`);
        
        // First, calculate CAPE for all tickers
        console.log('Calculating CAPE scores...');
        const capePromises = tickersToProcess.map((ticker: string) => calculateCAPE(ticker));
        const capeScores = await Promise.all(capePromises);
        console.log(`Calculated ${capeScores.length} CAPE scores`);
        
        // Calculate percentiles
        const capeWithPercentiles = calculateCAPEPercentiles(capeScores);
        
        // Create a map for easier lookup
        const capeMap = new Map<string, CapeScore>();
        capeWithPercentiles.forEach(score => {
          capeMap.set(score.ticker, score);
        });
        
        // Generate signals for each ticker
        console.log('Generating signals for each ticker...');
        const signalPromises = tickersToProcess.map(async (ticker: string) => {
          try {
            // Get financial data
            const data = await alphaVantageService.getFinancialData(ticker);
            
            // Calculate F-Score
            const fScore = await calculateFScore(ticker, data);
            
            // Generate P&F chart
            const pnfChart = generatePnFChart(ticker, data.priceHistory);
            
            // Get CAPE score with percentile
            const capeScore = capeMap.get(ticker) || {
              ticker,
              cape: 0,
              capePercentile: 50,
              date: new Date().toISOString().split('T')[0]
            };
            
            // Determine preliminary signal
            let preliminarySignal: SignalAction = 'Hold';
            
            if (capeScore.capePercentile <= 20 && fScore.score >= 6) {
              preliminarySignal = 'Buy';
            } else if (capeScore.capePercentile >= 80 && fScore.score <= 4) {
              preliminarySignal = 'Sell';
            }
            
            // Determine final signal
            let finalSignal: SignalAction = 'Hold';
            
            if (preliminarySignal === 'Buy' && pnfChart?.patterns[CONFIG.pnfPatternType === 'triple-top' ? 'tripleTopBreakout' : 'quadTopBreakout']) {
              finalSignal = 'Buy';
            } else if (preliminarySignal === 'Sell' && pnfChart?.patterns[CONFIG.pnfPatternType === 'triple-top' ? 'tripleTopBreakdown' : 'quadTopBreakdown']) {
              finalSignal = 'Sell';
            }
            
            return {
              ticker,
              cape: capeScore.cape,
              capePercentile: capeScore.capePercentile,
              f_score: fScore.score,
              preliminarySignal: preliminarySignal as SignalAction,
              finalSignal: finalSignal as SignalAction,
              date: new Date().toISOString().split('T')[0]
            };
          } catch (error) {
            console.error(`Error generating signal for ${ticker}:`, error);
            return {
              ticker,
              cape: 0,
              capePercentile: 50,
              f_score: 0,
              preliminarySignal: 'Hold' as SignalAction,
              finalSignal: 'Hold' as SignalAction,
              date: new Date().toISOString().split('T')[0]
            };
          }
        });
        
        // Wait for all signals to be generated
        const signals = await Promise.all(signalPromises);
        console.log(`Generated ${signals.length} signals total`);
        console.log(`Buy signals: ${signals.filter(s => s.finalSignal === 'Buy').length}`);
        console.log(`Sell signals: ${signals.filter(s => s.finalSignal === 'Sell').length}`);
        console.log(`Hold signals: ${signals.filter(s => s.finalSignal === 'Hold').length}`);
        
        // Store all signals in Supabase if available
        try {
          for (const signal of signals) {
            await supabaseService.tradingSignals.saveSignal(signal);
          }
        } catch (err) {
          console.warn('Could not save signals to database:', err);
        }
        
        return signals;
      },
      {
        ttl: 24 * 60 * 60 * 1000, // Cache for 24 hours
        forceRefresh: forceRefresh
      }
    );
  } catch (error) {
    console.error('Error generating all trading signals:', error);
    return [];
  }
};

/**
 * Get Buy signals based on the Triple Point strategy
 */
export const getBuySignals = async (): Promise<TradingSignal[]> => {
  try {
    const signals = await generateAllTradingSignals();
    return signals.filter(signal => signal.finalSignal === 'Buy');
  } catch (error) {
    console.error('Error fetching buy signals:', error);
    return [];
  }
};

/**
 * Get Sell signals based on the Triple Point strategy
 */
export const getSellSignals = async (): Promise<TradingSignal[]> => {
  try {
    const signals = await generateAllTradingSignals();
    return signals.filter(signal => signal.finalSignal === 'Sell');
  } catch (error) {
    console.error('Error fetching sell signals:', error);
    return [];
  }
};

// Export the trading strategy service
export const tradeStrategyService = {
  calculateCAPE,
  calculateFScore,
  generatePnFChart,
  generateTradingSignal,
  generateAllTradingSignals: (forceRefresh: boolean = false) => generateAllTradingSignals(forceRefresh),
  getBuySignals,
  getSellSignals,
  calculateCAPEPercentiles
};

export default tradeStrategyService; 